import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add,
  Assessment,
  Close,
  Delete,
  Event,
  ExpandMore,
  School,
  ContentCopy,
  LockOutlined,
  Subject as SubjectIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';

import {
  ExamGroupDetail,
  ExamGroupFormData,
  ExamGroupSubject,
  ExamType,
  PerClassEntry,
  Subject,
} from '../../types';
import { Session, SessionClass } from '../../types/sessions';
import { examService } from '../../services/examService';
import { curriculumApi } from '../../services/sessions/curriculumApi';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { fmtClassName } from '../../utils/pdf';

// ── Validation ───────────────────────────────────────────────────────────────
// Top-level required fields plus per-class invariants enforced via `.test()`.
const examFormSchema = yup.object({
  name: yup.string().trim().required('Exam name is required'),
  examTypeId: yup.string().required('Exam type is required'),
  sessionId: yup.string().required('Session is required'),
  startDate: yup.string().required('Start date is required'),
  endDate: yup
    .string()
    .required('End date is required')
    .test('after-start', 'End date must be on or after start date', function (value) {
      const { startDate } = this.parent;
      if (!startDate || !value) return true;
      return new Date(value) >= new Date(startDate);
    }),
  classIds: yup.array().of(yup.string()).min(1, 'Pick at least one class'),
  perClass: yup
    .array()
    .of(
      yup.object({
        classId: yup.string().required(),
        subjects: yup
          .array()
          .of(
            yup.object({
              subjectId: yup.string().required(),
              maxMarks: yup.number().min(1, 'Marks must be ≥ 1').required(),
            })
          )
          .min(1, 'Add at least one subject for this class'),
      })
    )
    .min(1, 'Add subjects for every selected class'),
});

interface FormValues {
  name: string;
  examTypeId: string;
  sessionId: string;
  startDate: string;
  endDate: string;
  classIds: string[];
  perClass: PerClassEntry[];
  countsInReports: boolean;
}

interface ExamFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingGroup: ExamGroupDetail | null;
  sessions: Session[];
  classes: SessionClass[];
  examTypes: ExamType[];
  activeSession?: { _id: string } | null;
}

const DEFAULT_MAX_MARKS = 100;

const ExamFormModal: React.FC<ExamFormModalProps> = ({
  open,
  onClose,
  onSaved,
  editingGroup,
  sessions,
  classes,
  examTypes,
  activeSession,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { enqueueSnackbar } = useSnackbar();

  const [subjectsByClass, setSubjectsByClass] = useState<Record<string, Subject[]>>({});
  // Tracks which classes have had their FULL subject list fetched. We
  // distinguish loaded from merely seeded (name-only) so the edit flow still
  // triggers a real fetch and surfaces additional subjects the user can add.
  const [loadedClasses, setLoadedClasses] = useState<Set<string>>(new Set());
  const [loadingClasses, setLoadingClasses] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | false>(false);
  const [problems, setProblems] = useState<string[]>([]);
  const problemsRef = useRef<HTMLDivElement>(null);
  // The server's refusals sit at the top of a long form — bring them into view.
  useEffect(() => { if (problems.length) problemsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, [problems]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const published = editingGroup?.status === 'published';

  // What is already recorded per (class, subject), so a published exam's
  // form can say what may still change.
  const recordedStats = useMemo(() => {
    const m = new Map<string, ExamGroupSubject>();
    for (const p of editingGroup?.perClass ?? []) for (const sub of p.subjects) m.set(`${p.class._id}|${sub.subjectId._id}`, sub);
    return m;
  }, [editingGroup]);
  const statsFor = (classId: string, subjectId: string) => (published ? recordedStats.get(`${classId}|${subjectId}`) : undefined);
  const classRecorded = (classId: string) =>
    published ? editingGroup!.perClass.find(p => p.class._id === classId)?.progress.recorded ?? 0 : 0;

  // ── Form ───────────────────────────────────────────────────────────────
  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      examTypeId: '',
      sessionId: activeSession?._id ?? '',
      startDate: '',
      endDate: '',
      classIds: [],
      perClass: [],
      countsInReports: true,
    },
    validationSchema: examFormSchema,
    onSubmit: async (values, helpers) => {
      setProblems([]);
      try {
        const payload: ExamGroupFormData = {
          name: values.name.trim(),
          examTypeId: values.examTypeId,
          sessionId: values.sessionId,
          startDate: values.startDate,
          endDate: values.endDate,
          countsInReports: values.countsInReports,
          perClass: values.perClass.map(p => ({
            classId: p.classId,
            subjects: p.subjects.map(s => ({
              subjectId: s.subjectId,
              maxMarks: Number(s.maxMarks) || 0,
            })),
          })),
        };

        if (editingGroup) {
          await examService.updateExamGroup(editingGroup.examGroupId, payload);
          enqueueSnackbar(`${payload.name} saved`, { variant: 'success' });
        } else {
          await examService.createExam(payload);
          enqueueSnackbar(`${payload.name} created as a draft`, { variant: 'success' });
        }
        onSaved();
        onClose();
      } catch (error: any) {
        // A published exam refuses changes its recorded marks contradict, naming each one.
        const listed: string[] = error?.details?.problems ?? [];
        if (listed.length) setProblems(listed);
        else enqueueSnackbar(error?.message || 'Could not save the exam', { variant: 'error' });
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  // ── Initialise / reset whenever the modal opens or editing target changes ─
  useEffect(() => {
    setProblems([]);
    if (!open) {
      // Forget per-class caches between sessions — schools tweak subject
      // assignments and we don't want stale lists.
      setLoadedClasses(new Set());
      setSubjectsByClass({});
      return;
    }
    if (editingGroup) {
      const classIds = editingGroup.perClass.map(p => p.class._id);
      const perClass: PerClassEntry[] = editingGroup.perClass.map(p => ({
        classId: p.class._id,
        subjects: p.subjects.map(s => ({
          subjectId: s.subjectId._id,
          maxMarks: s.maxMarks,
        })),
      }));
      // Seed the cache with names we already know so dropdowns don't briefly
      // show "—" while a real fetch is in flight; the fetch below upgrades
      // each class from "seeded" to "fully loaded".
      const seed: Record<string, Subject[]> = {};
      for (const p of editingGroup.perClass) {
        seed[p.class._id] = p.subjects.map(s => ({
          _id: s.subjectId._id,
          name: s.subjectId.name,
        } as Subject));
      }
      setSubjectsByClass(seed);

      formik.resetForm({
        values: {
          name: editingGroup.name,
          examTypeId: editingGroup.examType?._id ?? '',
          sessionId: editingGroup.session?._id ?? '',
          startDate: editingGroup.startDate?.split('T')[0] ?? '',
          endDate: editingGroup.endDate?.split('T')[0] ?? '',
          classIds,
          perClass,
          countsInReports: editingGroup.countsInReports,
        },
      });
      setExpanded(classIds[0] ?? false);

      // Kick off the real fetches in parallel so the "add more subjects"
      // chips populate with the FULL class curriculum (not just what's
      // already in the exam). Subjects come from CurriculumSubject, scoped
      // to (session, class).
      const editSessionId = editingGroup.session?._id;
      if (editSessionId) {
        Promise.all(classIds.map(c => ensureSubjectsLoaded(c, editSessionId)));
      }
    } else {
      formik.resetForm({
        values: {
          name: '',
          examTypeId: '',
          sessionId: activeSession?._id ?? '',
          startDate: '',
          endDate: '',
          classIds: [],
          perClass: [],
          countsInReports: true,
        },
      });
      setExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingGroup?.examGroupId]);

  // ── Fetch subjects for newly-added classes; default-populate perClass ────
  // Subjects come from the *curriculum* (CurriculumSubject) — the per-session
  // per-class assignment — NOT the global Subject directory. The curriculum
  // endpoint already merges common + section-specific rows and dedupes.
  const ensureSubjectsLoaded = useCallback(
    async (classId: string, sessionId: string) => {
      if (!sessionId) return [];
      if (loadedClasses.has(classId)) return subjectsByClass[classId] ?? [];
      setLoadingClasses(prev => new Set(prev).add(classId));
      try {
        const list = await curriculumApi.listForExam({ sessionId, classId });
        const coerced: Subject[] = list.map(s => ({ _id: s._id, name: s.name } as Subject));
        setSubjectsByClass(prev => ({ ...prev, [classId]: coerced }));
        setLoadedClasses(prev => new Set(prev).add(classId));
        return coerced;
      } catch (error: any) {
        enqueueSnackbar(`Failed to load curriculum subjects for class`, { variant: 'error' });
        return [];
      } finally {
        setLoadingClasses(prev => {
          const next = new Set(prev);
          next.delete(classId);
          return next;
        });
      }
    },
    [loadedClasses, subjectsByClass, enqueueSnackbar]
  );

  // When the multi-class picker changes, sync the perClass list:
  //  - new classes get an entry pre-populated with ALL their curriculum subjects
  //  - removed classes get their entry pruned
  // The session must be picked first — subjects are scoped to (session, class).
  const syncPerClassWith = useCallback(
    async (nextClassIds: string[]) => {
      const kept = formik.values.classIds.filter(c => !nextClassIds.includes(c) && classRecorded(c) > 0);
      if (kept.length) {
        enqueueSnackbar(`${kept.map(c => classNameById.get(c) ?? 'A class').join(', ')} can't be removed — marks are recorded for it.`, { variant: 'warning' });
        nextClassIds = [...nextClassIds, ...kept];
      }
      const sessionId = formik.values.sessionId;
      const existing = new Map(formik.values.perClass.map(p => [p.classId, p]));
      const newClassIds = nextClassIds.filter(c => !existing.has(c));

      // Load curriculum subjects for newly-added classes in parallel.
      const loaded = await Promise.all(
        newClassIds.map(c => ensureSubjectsLoaded(c, sessionId))
      );

      const merged: PerClassEntry[] = nextClassIds.map(classId => {
        const prev = existing.get(classId);
        if (prev) return prev;
        const subjectsForClass = loaded[newClassIds.indexOf(classId)] ?? [];
        return {
          classId,
          subjects: subjectsForClass.map(s => ({
            subjectId: s._id,
            maxMarks: DEFAULT_MAX_MARKS,
          })),
        };
      });

      formik.setFieldValue('classIds', nextClassIds);
      formik.setFieldValue('perClass', merged);

      // Auto-expand the most recently added class for elegant guidance.
      if (newClassIds.length > 0) setExpanded(newClassIds[newClassIds.length - 1]);
      else if (nextClassIds.length === 1) setExpanded(nextClassIds[0]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.perClass, formik.values.classIds, formik.values.sessionId, ensureSubjectsLoaded]
  );

  // Switching the session invalidates every per-class subject list (each
  // class's curriculum is session-scoped). Wipe caches and ask the user to
  // re-select classes rather than silently leaving stale subjects in the form.
  const handleSessionChange = (nextSessionId: string) => {
    formik.setFieldValue('sessionId', nextSessionId);
    if (formik.values.classIds.length > 0) {
      setSubjectsByClass({});
      setLoadedClasses(new Set());
      formik.setFieldValue('classIds', []);
      formik.setFieldValue('perClass', []);
      setExpanded(false);
      enqueueSnackbar(
        'Session changed — please re-pick classes; subjects are scoped to the session.',
        { variant: 'info' }
      );
    }
  };

  // ── Per-class helpers ────────────────────────────────────────────────────
  const updateClassEntry = (classId: string, updater: (entry: PerClassEntry) => PerClassEntry) => {
    const next = formik.values.perClass.map(p => (p.classId === classId ? updater(p) : p));
    formik.setFieldValue('perClass', next);
  };

  const setSubjectMaxMarks = (classId: string, subjectId: string, value: number) => {
    updateClassEntry(classId, p => ({
      ...p,
      subjects: p.subjects.map(s => (s.subjectId === subjectId ? { ...s, maxMarks: value } : s)),
    }));
  };

  const removeSubjectRow = (classId: string, subjectId: string) => {
    updateClassEntry(classId, p => ({
      ...p,
      subjects: p.subjects.filter(s => s.subjectId !== subjectId),
    }));
  };

  const addSubjectRow = (classId: string, subjectId: string) => {
    updateClassEntry(classId, p => {
      if (p.subjects.some(s => s.subjectId === subjectId)) return p;
      return {
        ...p,
        subjects: [...p.subjects, { subjectId, maxMarks: DEFAULT_MAX_MARKS }],
      };
    });
  };

  // Apply the max-marks of this row to every other class that has the same
  // subject. The common-case ergonomic win.
  const applyMarksToAll = (sourceClassId: string, subjectId: string, marks: number) => {
    let touched = 0;
    const next = formik.values.perClass.map(p => {
      if (p.classId === sourceClassId) return p;
      let didTouch = false;
      const subs = p.subjects.map(s => {
        if (s.subjectId === subjectId && s.maxMarks !== marks) {
          didTouch = true;
          return { ...s, maxMarks: marks };
        }
        return s;
      });
      if (didTouch) touched += 1;
      return didTouch ? { ...p, subjects: subs } : p;
    });
    formik.setFieldValue('perClass', next);
    enqueueSnackbar(
      touched > 0
        ? `Applied ${marks} marks to ${touched} other class(es)`
        : 'No other class has this subject',
      { variant: touched > 0 ? 'success' : 'info' }
    );
  };

  // ── Derived helpers ──────────────────────────────────────────────────────
  const classNameById = useMemo(() => {
    const m = new Map<string, string>();
    classes.forEach(c => m.set(c._id, fmtClassName(c.name)));
    return m;
  }, [classes]);

  const subjectNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const list of Object.values(subjectsByClass)) {
      for (const s of list) m.set(s._id, s.name);
    }
    return m;
  }, [subjectsByClass]);

  const totalMarksFor = (entry: PerClassEntry) =>
    entry.subjects.reduce((sum, s) => sum + (Number(s.maxMarks) || 0), 0);

  // Closing (Escape, the backdrop, ×) never drops a filled-in form silently.
  const handleClose = () => {
    if (formik.isSubmitting) return;
    if (formik.dirty) setConfirmDiscard(true);
    else onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxHeight: '92vh',
        },
      }}
    >
      <DialogTitle component="div"
        sx={{
          pb: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Assessment sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingGroup ? 'Edit Exam' : 'Create New Exam'}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {published && (
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, mt: 1 }}>
            This exam is published. You can rename it, change its dates and maximum marks, and add subjects or classes.
            Subjects and classes with marks recorded stay; submitted subjects need unlocking before their maximum changes.
          </Alert>
        )}
        {problems.length > 0 && (
          <Alert ref={problemsRef} severity="error" sx={{ borderRadius: 2, mt: 1.5 }} onClose={() => setProblems([])}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Nothing was saved:</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {problems.map(p => <li key={p}><Typography variant="body2">{p}</Typography></li>)}
            </Box>
          </Alert>
        )}
        {/* Basic Information */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <CardContent>
            <Typography
              variant="subtitle1"
              sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <School fontSize="small" color="primary" />
              Basic Information
            </Typography>

            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="name"
                  label="Exam Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  error={formik.touched.examTypeId && Boolean(formik.errors.examTypeId)}
                >
                  <InputLabel>Exam Type</InputLabel>
                  <Select
                    name="examTypeId"
                    value={formik.values.examTypeId}
                    onChange={e => {
                      formik.handleChange(e);
                      // A new exam takes the type's reporting default; an existing one keeps its choice.
                      const type = examTypes.find(t => t._id === e.target.value);
                      if (!editingGroup && type) formik.setFieldValue('countsInReports', type.countsInReportsByDefault !== false);
                    }}
                    label="Exam Type"
                    sx={{ borderRadius: 2 }}
                  >
                    {examTypes.length === 0 ? (
                      <MenuItem value="" disabled>
                        No exam types configured
                      </MenuItem>
                    ) : (
                      examTypes.map(type => (
                        <MenuItem key={type._id} value={type._id}>
                          {type.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {formik.touched.examTypeId && formik.errors.examTypeId && <FormHelperText>{formik.errors.examTypeId}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  error={formik.touched.sessionId && Boolean(formik.errors.sessionId)}
                >
                  <InputLabel>Session</InputLabel>
                  <Select
                    name="sessionId"
                    disabled={published}
                    value={formik.values.sessionId}
                    onChange={e => handleSessionChange(String(e.target.value))}
                    label="Session"
                    sx={{ borderRadius: 2 }}
                    renderValue={(val) => {
                      if (!val) return '';
                      const sel = sessions.find(s => s._id === val);
                      if (!sel) return '';
                      const isActive = activeSession?._id === sel._id;
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <span>{sel.name}</span>
                          {isActive && (
                            <Chip
                              label="Active"
                              size="small"
                              color="success"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                letterSpacing: 0.3,
                                color: '#fff',
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                  >
                    {sessions.map(s => {
                      const isActive = activeSession?._id === s._id;
                      return (
                        <MenuItem key={s._id} value={s._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                            <span>{s.name}</span>
                            {isActive && (
                              <Chip
                                label="Active"
                                size="small"
                                color="success"
                                sx={{
                                  ml: 'auto',
                                  height: 18,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  letterSpacing: 0.3,
                                  color: '#fff',
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  disabled={!formik.values.sessionId}
                  options={classes}
                  getOptionLabel={option => fmtClassName(option.name)}
                  value={classes.filter(c => formik.values.classIds.includes(c._id))}
                  onChange={(_, newValue) => syncPerClassWith(newValue.map(v => v._id))}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Classes"
                      placeholder={
                        !formik.values.sessionId
                          ? 'Pick a session first'
                          : classes.length === 0
                            ? 'No classes available'
                            : 'Pick one or more classes'
                      }
                      error={formik.touched.classIds && Boolean(formik.errors.classIds)}
                      helperText={
                        (formik.touched.classIds && (formik.errors.classIds as string)) ||
                        'Subjects are pulled from each class\'s curriculum for the selected session.'
                      }
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  name="startDate"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.startDate}
                  onChange={formik.handleChange}
                  error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                  helperText={formik.touched.startDate && formik.errors.startDate}
                  InputProps={{
                    startAdornment: <Event sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  name="endDate"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.endDate}
                  onChange={formik.handleChange}
                  error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                  helperText={formik.touched.endDate && formik.errors.endDate}
                  InputProps={{
                    startAdornment: <Event sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={<Switch checked={formik.values.countsInReports} onChange={e => formik.setFieldValue('countsInReports', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Counts in reports</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Shown on transcripts and mark sheets, and included in averages and promotion. Turn off for practice tests and quizzes.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0, gap: 1, '& .MuiSwitch-root': { mt: -0.5 } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Per-class subjects */}
        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <SubjectIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                Subjects &amp; Marks
              </Typography>
              {formik.values.perClass.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {formik.values.perClass.length} class
                  {formik.values.perClass.length === 1 ? '' : 'es'}
                </Typography>
              )}
            </Box>

            {formik.values.classIds.length === 0 ? (
              <Alert
                severity="info"
                variant="outlined"
                sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.875rem' } }}
              >
                Pick at least one class above — we'll pre-load each class's subjects so you only need
                to tweak the marks.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {formik.values.perClass.map(entry => {
                  const className = classNameById.get(entry.classId) ?? '—';
                  const available = subjectsByClass[entry.classId] ?? [];
                  const isLoading = loadingClasses.has(entry.classId);
                  const selectedIds = new Set(entry.subjects.map(s => s.subjectId));
                  const unaddedSubjects = available.filter(s => !selectedIds.has(s._id));
                  const total = totalMarksFor(entry);

                  return (
                    <Accordion
                      key={entry.classId}
                      expanded={expanded === entry.classId}
                      onChange={(_, isExp) => setExpanded(isExp ? entry.classId : false)}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { borderColor: 'primary.main' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          px: 2,
                          minHeight: 56,
                          bgcolor:
                            expanded === entry.classId
                              ? alpha(theme.palette.primary.main, isDark ? 0.12 : 0.05)
                              : 'transparent',
                          '& .MuiAccordionSummary-content': { my: 1.25, alignItems: 'center', gap: 1.5 },
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1),
                            color: 'primary.main',
                          }}
                        >
                          <School fontSize="small" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {className}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.subjects.length} subject{entry.subjects.length === 1 ? '' : 's'}
                            {entry.subjects.length > 0 && ` · Total ${total} marks`}
                          </Typography>
                        </Box>
                        {entry.subjects.length === 0 && (
                          <Chip
                            label="No subjects"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              bgcolor: alpha(theme.palette.warning.main, 0.12),
                              color: 'warning.main',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0, borderTop: '1px solid', borderColor: 'divider' }}>
                        {isLoading && <LinearProgress />}

                        {!isLoading && available.length === 0 && (
                          <Box sx={{ p: 2.5 }}>
                            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                              No subjects are assigned to {className}. Assign subjects first from
                              the Subjects settings.
                            </Alert>
                          </Box>
                        )}

                        {!isLoading && available.length > 0 && (
                          <Box sx={{ p: 2 }}>
                            {entry.subjects.length === 0 ? (
                              <Alert
                                severity="info"
                                variant="outlined"
                                sx={{ borderRadius: 2, mb: 1.5, '& .MuiAlert-message': { fontSize: '0.85rem' } }}
                              >
                                Add the subjects you want to include in this exam.
                              </Alert>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {entry.subjects.map(row => {
                                  const subjectName =
                                    subjectNameById.get(row.subjectId) ??
                                    available.find(a => a._id === row.subjectId)?.name ??
                                    '—';
                                  const stats = statsFor(entry.classId, row.subjectId);
                                  return (
                                    <Box
                                      key={row.subjectId}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        p: 1.25,
                                        borderRadius: 2,
                                        bgcolor: isDark
                                          ? alpha(theme.palette.text.primary, 0.03)
                                          : alpha('#000', 0.015),
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        transition: 'border-color 0.15s ease',
                                        '&:hover': { borderColor: 'primary.main' },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 6,
                                          height: 26,
                                          borderRadius: 1,
                                          bgcolor: theme.palette.primary.main,
                                          opacity: 0.5,
                                        }}
                                      />
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {subjectName}
                                        </Typography>
                                        {stats && stats.recorded > 0 && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {stats.locked > 0 && <LockOutlined sx={{ fontSize: 13 }} />}
                                            {stats.recorded} of {stats.papers} marked{stats.locked > 0 ? ' · submitted' : ''}
                                            {stats.highest != null ? ` · highest ${stats.highest}` : ''}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Tooltip title={stats?.locked ? 'Its marks are submitted — unlock them in Enter Marks to change the maximum' : ''} arrow>
                                      <span>
                                      <TextField
                                        type="number"
                                        size="small"
                                        label="Max Marks"
                                        value={row.maxMarks}
                                        onChange={e =>
                                          setSubjectMaxMarks(
                                            entry.classId,
                                            row.subjectId,
                                            Number(e.target.value) || 0
                                          )
                                        }
                                        disabled={!!stats?.locked}
                                        error={stats?.highest != null && Number(row.maxMarks) < stats.highest}
                                        inputProps={{ min: Math.max(1, stats?.highest ?? 1), step: 'any', style: { textAlign: 'right' } }}
                                        sx={{
                                          width: 130,
                                          '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                                        }}
                                      />
                                      </span>
                                      </Tooltip>
                                      <Tooltip
                                        title="Apply these marks to every other class that has this subject"
                                        arrow
                                      >
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              applyMarksToAll(
                                                entry.classId,
                                                row.subjectId,
                                                Number(row.maxMarks) || 0
                                              )
                                            }
                                            disabled={formik.values.perClass.length < 2}
                                            sx={{
                                              color: 'primary.main',
                                              '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                              },
                                            }}
                                          >
                                            <ContentCopy fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title={stats?.recorded ? `${stats.recorded} student${stats.recorded === 1 ? ' has' : 's have'} marks in it` : 'Remove this subject'} arrow>
                                        <span>
                                        <IconButton
                                          size="small"
                                          disabled={!!stats?.recorded}
                                          onClick={() =>
                                            removeSubjectRow(entry.classId, row.subjectId)
                                          }
                                          sx={{
                                            color: 'error.main',
                                            '&:hover': {
                                              bgcolor: alpha(theme.palette.error.main, 0.1),
                                            },
                                          }}
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}

                            {/* Add another subject (from the class's pool, excluding already-added) */}
                            {unaddedSubjects.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                                {unaddedSubjects.map(s => (
                                  <Chip
                                    key={s._id}
                                    icon={<Add fontSize="small" />}
                                    label={s.name}
                                    size="small"
                                    onClick={() => addSubjectRow(entry.classId, s._id)}
                                    sx={{
                                      borderRadius: 1.5,
                                      cursor: 'pointer',
                                      borderColor: 'divider',
                                      bgcolor: 'transparent',
                                      border: '1px dashed',
                                      color: 'text.secondary',
                                      fontWeight: 500,
                                      '& .MuiChip-icon': { color: 'primary.main', ml: 0.5 },
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        borderColor: 'primary.main',
                                        color: 'primary.main',
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          onClick={handleClose}
          disabled={formik.isSubmitting}
          sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => formik.handleSubmit()}
          variant="contained"
          disabled={formik.isSubmitting}
          sx={{ borderRadius: 2, textTransform: 'none', px: 3, fontWeight: 600 }}
        >
          {formik.isSubmitting ? 'Saving…' : editingGroup ? 'Save changes' : 'Create exam'}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
        title="Discard this exam?"
        message="What you've entered hasn't been saved."
        confirmText="Discard"
        cancelText="Keep editing"
      />
    </Dialog>
  );
};

export default ExamFormModal;
